import { useState } from 'react';
import { ChevronDown, ChevronRight, Check, Loader2 } from 'lucide-react';
import { PIPELINE_TYPE_LABELS } from '../types';
import { STAGE_COLOR_PRESETS } from '../hooks/usePipelineStages';
import type { PipelineWithStages } from '../hooks/usePipelinesWithStages';

interface PipelineStageMoverPopoverProps {
  pipelinesWithStages: PipelineWithStages[];
  loading: boolean;
  currentPipelineId: string;
  currentStageId: string;
  movingStageId?: string | null;
  onSelectStage: (pipelineId: string, stageId: string) => void;
}

export function PipelineStageMoverPopover({
  pipelinesWithStages,
  loading,
  currentPipelineId,
  currentStageId,
  movingStageId,
  onSelectStage,
}: PipelineStageMoverPopoverProps) {
  const [expandedId, setExpandedId] = useState<string | null>(currentPipelineId);

  return (
    <div className="w-80 max-h-[420px] flex flex-col overflow-hidden">
      <div className="px-1 pb-2">
        <h4 className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-widest">
          Mover para
        </h4>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 -mx-1 px-1">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        ) : pipelinesWithStages.length === 0 ? (
          <p className="text-xs text-gray-400 px-2 py-4 text-center">Nenhuma pipeline disponível</p>
        ) : (
          <div className="space-y-1">
            {pipelinesWithStages.map(({ pipeline, stages }) => {
              const isExpanded = expandedId === pipeline.id;
              const isCurrentPipeline = pipeline.id === currentPipelineId;
              const visibleStages = stages.filter((stage) => !stage.oculta);

              return (
                <div key={pipeline.id} className="rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : pipeline.id)}
                    className="w-full flex items-center gap-2 px-2 py-2 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/50 rounded-lg transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="flex-1 min-w-0">
                      <span
                        className={`block truncate text-[13px] font-semibold ${
                          isCurrentPipeline
                            ? 'text-blue-700 dark:text-blue-300'
                            : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {pipeline.nome}
                      </span>
                      <span className="block text-[11px] text-gray-400 mt-0.5">
                        {PIPELINE_TYPE_LABELS[pipeline.tipo] || pipeline.tipo}
                      </span>
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="pl-7 pr-1 pb-1 space-y-0.5">
                      {visibleStages.length === 0 ? (
                        <p className="text-[11px] text-gray-400 py-1.5">Sem etapas</p>
                      ) : (
                        visibleStages.map((stage, index) => {
                          const isCurrentStage =
                            isCurrentPipeline && stage.id === currentStageId;
                          const isMoving = movingStageId === stage.id;
                          const color = stage.cor || STAGE_COLOR_PRESETS[index % STAGE_COLOR_PRESETS.length];

                          return (
                            <button
                              key={stage.id}
                              type="button"
                              disabled={isCurrentStage || !!movingStageId}
                              onClick={() => onSelectStage(pipeline.id, stage.id)}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-[12.5px] transition-colors ${
                                isCurrentStage
                                  ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 cursor-default'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-zinc-800/50 disabled:opacity-50 disabled:cursor-not-allowed'
                              }`}
                            >
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: color }}
                              />
                              <span className="flex-1 truncate">{stage.nome}</span>
                              {isCurrentStage && (
                                <Check className="w-3.5 h-3.5 flex-shrink-0" />
                              )}
                              {isMoving && (
                                <Loader2 className="w-3.5 h-3.5 flex-shrink-0 animate-spin" />
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PipelineStageMoverPopover;
